// Role checks that treat both spellings of a role as the same role.
//
// The cpass role enum is mid-rename and carries both names for five roles. Every
// real user still holds the LEGACY name, and sign-in now also grants the twin, so
// a screen that lists only one spelling either hides a control from everybody or
// breaks the day users are migrated. The backend has no role hierarchy either:
// each @PreAuthorize list is literal, and so is each list here.
//
// The portal holds roles lower-cased ('operator_admin'); the server names them
// upper-cased, sometimes with a ROLE_ prefix. Both are accepted.

/** Legacy name -> current name. Read in both directions. */
export const ROLE_TWINS: Record<string, string> = {
  master_admin: 'platform_admin',
  super_admin: 'provider_owner',
  service_admin: 'provider_admin',
  operator_admin: 'business_admin',
  operator_user: 'business_user',
};

const TWIN_OF: Record<string, string> = Object.keys(ROLE_TWINS).reduce<Record<string, string>>((acc, legacy) => {
  acc[legacy] = ROLE_TWINS[legacy];
  acc[ROLE_TWINS[legacy]] = legacy;
  return acc;
}, {});

/** 'ROLE_OPERATOR_ADMIN', 'OPERATOR_ADMIN' and 'operator_admin' are one role. */
export const normalizeRole = (role: string | null | undefined): string =>
  (role || '').trim().toLowerCase().replace(/^role_/, '');

/** The role and its twin, if it has one. */
export const roleSpellings = (role: string | null | undefined): string[] => {
  const r = normalizeRole(role);
  if (!r) return [];
  return TWIN_OF[r] ? [r, TWIN_OF[r]] : [r];
};

/** True when `role`, under either of its spellings, is in `allowed`. */
export const roleIn = (role: string | null | undefined, allowed: readonly string[]): boolean => {
  const wanted = allowed.map(normalizeRole);
  return roleSpellings(role).some((r) => wanted.includes(r));
};

/** The role a page acts with: the selected organisation's, else the user's own. */
export const actingRole = (auth: { user?: any; currentOrganization?: any } | null | undefined): string =>
  normalizeRole(auth?.currentOrganization?.role || auth?.user?.role);
